import { useEffect, useMemo, useRef } from 'react'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Box, Chip, CircularProgress, Typography } from '@mui/material'
import { groupNotificationsByDay } from './notification-day-group.util'
import { NotificationListItem } from './notification-list-item'
import { NOTIFICATION_DAY_GROUPS } from './notification-center.const'

const DAY_GROUP_LABELS = {
  [NOTIFICATION_DAY_GROUPS.TODAY]: msg`Today`,
  [NOTIFICATION_DAY_GROUPS.YESTERDAY]: msg`Yesterday`,
  [NOTIFICATION_DAY_GROUPS.EARLIER]: msg`Earlier`,
}

function useInfiniteScroll(rootRef, onLoadMore) {
  const sentinelRef = useRef(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver(
      entries => { if (entries.some(entry => entry.isIntersecting)) onLoadMore() },
      { root: rootRef.current, rootMargin: '120px' },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [rootRef, onLoadMore])

  return sentinelRef
}

function NotificationListStatus({ isLoading, hasError, isEmpty }) {
  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
  if (hasError) return <Typography sx={{ textAlign: 'center', py: 3, color: 'error.main' }}><Trans>Unable to load notifications.</Trans></Typography>
  if (isEmpty) return <Typography sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}><Trans>No notifications found.</Trans></Typography>
  return null
}

function formatGroupCount(group, isPartial) {
  return isPartial ? `${group.items.length}+` : group.items.length
}

/**
 * Notifications grouped by day with infinite scroll.
 * @param {{ items: object[], isLoading: boolean, hasError: boolean, hasMore: boolean, getViewAvailability: (n: object) => string, onLoadMore: () => void, onSelect: (n: object) => void, onView: (n: object) => void }} props
 */
export function NotificationList({ items, isLoading, hasError, hasMore, getViewAvailability, onLoadMore, onSelect, onView }) {
  const { _ } = useLingui()
  const scrollRef = useRef(null)
  const sentinelRef = useInfiniteScroll(scrollRef, onLoadMore)
  const groups = useMemo(() => groupNotificationsByDay(items), [items])

  return (
    <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2 }}>
      {groups.map((group, index) => (
        <Box component="section" key={group.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{_(DAY_GROUP_LABELS[group.id])}</Typography>
            <Chip size="small" label={formatGroupCount(group, hasMore && index === groups.length - 1)} />
          </Box>
          {group.items.map(notification => (
            <NotificationListItem
              key={notification.id}
              notification={notification}
              viewAvailability={getViewAvailability(notification)}
              onSelect={onSelect}
              onView={onView}
            />
          ))}
        </Box>
      ))}
      <Box ref={sentinelRef} sx={{ height: 1 }} />
      <NotificationListStatus isLoading={isLoading} hasError={hasError && items.length === 0} isEmpty={!isLoading && items.length === 0} />
    </Box>
  )
}
